/**
 * Pick an emotional theme from emotional signals, deterministically per seed.
 *
 * Two announcements with the same tone can still land on different themes —
 * the rng makes the choice stable per announcement but varied across them.
 */

import type { EmotionalSignals, EmotionalTone } from './emotionalSignals';
import type { EmotionalThemeId } from './emotionalThemes';
import type { SeededRng } from './revealSeed';

const TONE_WEIGHTS: Record<EmotionalTone, Partial<Record<EmotionalThemeId, number>>> = {
  warm: {
    warm_nostalgia: 0.30, golden_memory: 0.25, soul_connection: 0.15,
    gentle_healing: 0.10, eternal_bond: 0.10, inner_peace: 0.10,
  },
  reflective: {
    midnight_reflection: 0.30, inner_peace: 0.20, through_the_years: 0.15,
    legacy: 0.15, warm_nostalgia: 0.10, heavenlight: 0.10,
  },
  celebratory: {
    celebration_pulse: 0.40, light_after_darkness: 0.20, through_the_years: 0.15,
    golden_memory: 0.15, eternal_bond: 0.10,
  },
  apologetic: {
    gentle_healing: 0.30, midnight_reflection: 0.20, light_after_darkness: 0.20,
    inner_peace: 0.15, soul_connection: 0.15,
  },
  romantic: {
    soul_connection: 0.30, eternal_bond: 0.25, golden_memory: 0.15,
    warm_nostalgia: 0.15, celebration_pulse: 0.15,
  },
  closure: {
    heavenlight: 0.30, midnight_reflection: 0.25, inner_peace: 0.20,
    light_after_darkness: 0.15, legacy: 0.10,
  },
  legacy: {
    legacy: 0.40, through_the_years: 0.25, heavenlight: 0.15,
    golden_memory: 0.10, midnight_reflection: 0.10,
  },
  gratitude: {
    warm_nostalgia: 0.25, golden_memory: 0.25, gentle_healing: 0.15,
    eternal_bond: 0.15, soul_connection: 0.10, light_after_darkness: 0.10,
  },
  healing: {
    gentle_healing: 0.35, light_after_darkness: 0.25, inner_peace: 0.20,
    heavenlight: 0.10, soul_connection: 0.10,
  },
};

export function pickEmotionalTheme(
  signals: EmotionalSignals,
  rng: SeededRng,
  relationshipBias: EmotionalThemeId[] = [],
): EmotionalThemeId {
  const map = TONE_WEIGHTS[signals.tone] ?? TONE_WEIGHTS.warm;
  const ids = Object.keys(map) as EmotionalThemeId[];
  let weights = ids.map((id) => map[id] ?? 0);

  // Bias by intimacy
  if (signals.intimacy === 'close') {
    weights = weights.map((w, i) =>
      w * (['soul_connection', 'eternal_bond', 'golden_memory'].includes(ids[i]) ? 1.4 : 1));
  }

  // Bias by energy
  if (signals.energy === 'still') {
    weights = weights.map((w, i) =>
      w * (['midnight_reflection', 'inner_peace', 'legacy', 'heavenlight'].includes(ids[i]) ? 1.3 : 1));
  } else if (signals.energy === 'uplifting') {
    weights = weights.map((w, i) =>
      w * (['celebration_pulse', 'light_after_darkness', 'through_the_years'].includes(ids[i]) ? 1.3 : 1));
  }

  // Bias by time of day
  if (signals.timeOfDay === 'night') {
    weights = weights.map((w, i) =>
      w * (['midnight_reflection', 'heavenlight', 'soul_connection'].includes(ids[i]) ? 1.25 : 1));
  } else if (signals.timeOfDay === 'dawn') {
    weights = weights.map((w, i) =>
      w * (['light_after_darkness', 'gentle_healing', 'inner_peace'].includes(ids[i]) ? 1.25 : 1));
  }

  // Relationship bias — strongest pull, since the recipient defines the moment.
  if (relationshipBias.length) {
    weights = weights.map((w, i) => (relationshipBias.includes(ids[i]) ? w * 1.8 : w));
  }

  return rng.weighted(ids, weights);
}
